import { KeyValuePipe } from '@angular/common';
import { Component, DestroyRef, effect, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { INSTITUTS_MÉTIER } from '@shared/models/instituts-métier';
import { LivrablePrincipal } from '@shared/models/livrable-principal.enum';
import { TrierParPipe } from '@shared/pipes/trier-par.pipe';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmSelectImports } from '@spartan-ng/helm/select';

import { HlmButtonImports } from '@spartan-ng/helm/button';
import { CartographieStore } from '../../../cartographie/store/cartographie.store';

import { RéférentielLivrableStore } from '../../store/référentiel-livrable.store';
import {
  ÉtatRéférentielLivrable,
  ÉtatRéférentielLivrableNom,
} from '../../../administration/gestion-référentiels/models/référentiel-livrable.model';
import { REFERENTIEL_TYPE } from '../../../administration/gestion-référentiels/models/référentiel-type.enum';
import { ConvertReferentielTypeToStringPipe } from '../../../administration/gestion-référentiels/pipes/convert-referentiel-type-to-string-pipe';

@Component({
  selector: 'app-filtres-suivi-livrables',
  imports: [
    ReactiveFormsModule,
    HlmFieldImports,
    HlmSelectImports,
    BrnSelectImports,
    TrierParPipe,
    ConvertReferentielTypeToStringPipe,
    KeyValuePipe,
    HlmButtonImports,
  ],
  templateUrl: './filtres-suivi-livrables.component.html',
})
export class FiltresSuiviLivrablesComponent implements OnInit {
  readonly #referentielLivrableStore = inject(RéférentielLivrableStore);
  readonly #cartographieStore = inject(CartographieStore);
  readonly #fb = inject(FormBuilder);
  readonly #destroyRef = inject(DestroyRef);

  readonly cartographies = this.#cartographieStore.actives;

  readonly referentielTypes = REFERENTIEL_TYPE;
  readonly institutsMetier = INSTITUTS_MÉTIER;
  readonly livrablesPrincipaux = LivrablePrincipal;
  readonly etats = {
    [ÉtatRéférentielLivrable.NON_DEMARRE]: ÉtatRéférentielLivrableNom.NON_DEMARRE,
    [ÉtatRéférentielLivrable.PRODUCTION_DEMARREE]: ÉtatRéférentielLivrableNom.PRODUCTION_DEMARREE,
    [ÉtatRéférentielLivrable.VALIDE_METIER]: ÉtatRéférentielLivrableNom.VALIDE_METIER,
    [ÉtatRéférentielLivrable.VALIDE_SERVICE_FORMATION]: ÉtatRéférentielLivrableNom.VALIDE_SERVICE_FORMATION,
    [ÉtatRéférentielLivrable.VALIDE_CDM]: ÉtatRéférentielLivrableNom.VALIDE_CDM,
    [ÉtatRéférentielLivrable.NON_CONCERNE]: ÉtatRéférentielLivrableNom.NON_CONCERNE,
  };

  readonly form = this.#fb.group({
    cartographies: [[] as string[]],
    institutsMetiers: [[] as string[]],
    services: [[] as string[]],
    livrables: [[] as LivrablePrincipal[]],
    statuts: [[] as string[]],
    echeance: [''],
  });

  constructor() {
    effect(() => {
      const filtres = this.#referentielLivrableStore.filtres();

      this.form.patchValue(
        {
          cartographies: filtres?.cartographies,
          institutsMetiers: filtres?.institutsMetiers,
          services: filtres?.services,
          livrables: filtres?.livrables,
          echeance: filtres?.echeance,
          statuts: filtres?.statuts,
        },
        { emitEvent: false },
      );
    });
  }

  ngOnInit(): void {
    this.#cartographieStore.charger();

    this.form.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onSubmit());
  }

  onSubmit() {
    this.#referentielLivrableStore.setLoading(true);
    setTimeout(() => {
      this.#referentielLivrableStore.setFiltres({
        cartographies: this.cartographiesFC.value,
        institutsMetiers: this.institutsMetiers.value,
        services: this.services.value,
        livrables: this.livrables.value,
        echeance: this.echeance.value,
        statuts: this.statuts.value,
      });
      this.#referentielLivrableStore.setLoading(false);
    }, 500);
  }

  get cartographiesFC(): AbstractControl<string[]> {
    return this.form.get('cartographies') as FormControl<string[]>;
  }

  get institutsMetiers(): AbstractControl<string[]> {
    return this.form.get('institutsMetiers') as FormControl<string[]>;
  }

  get services(): AbstractControl<string[]> {
    return this.form.get('services') as FormControl<string[]>;
  }

  get livrables(): AbstractControl<LivrablePrincipal[]> {
    return this.form.get('livrables') as FormControl<LivrablePrincipal[]>;
  }

  get echeance(): AbstractControl<'ONE_WEEK' | 'ONE_MONTH' | 'THREE_MONTHS'> {
    return this.form.get('echeance') as FormControl<'ONE_WEEK' | 'ONE_MONTH' | 'THREE_MONTHS'>;
  }

  get statuts(): AbstractControl<ÉtatRéférentielLivrable[]> {
    return this.form.get('statuts') as FormControl<ÉtatRéférentielLivrable[]>;
  }

  get estMetierSelectionne(): boolean {
    return this.services.value?.includes('Métier');
  }
}
